/**
 * Who is signed in, and for how long.
 *
 * One context owns three things that are easy to let drift apart: the Cognito session,
 * the bearer token the API client reads, and the two session deadlines. Keeping them
 * together means a sign-out cannot leave a stale token behind for the next fetch.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  associateWebAuthnCredential,
  confirmSignIn,
  fetchAuthSession,
  fetchMFAPreference,
  getCurrentUser,
  listWebAuthnCredentials,
  setUpTOTP,
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  updateMFAPreference,
  verifyTOTPSetup,
  type SignInOutput,
} from 'aws-amplify/auth';

import { forgetThisBrowser } from '@/features/notifications/webPush';
import { setTokenProvider } from '@/lib/api';

import {
  ABSOLUTE_LIMIT_MS,
  authConfigured,
  DEV_USER,
  IDLE_LIMIT_MS,
  IDLE_WARNING_MS,
  SESSION_END_REASON_KEY,
  SESSION_START_KEY,
} from './config';

/** Why a session ended, so the sign-in page can explain rather than just reappear. */
export type EndReason = 'idle' | 'absolute' | 'noAccess' | null;

/**
 * What the sign-in form should be showing.
 *
 * Cognito drives this: `signIn` may return "done" or it may return a challenge, and
 * the form's job is to render whichever step it is on rather than assume the happy
 * path and break on the first account that has MFA.
 */
export type Step =
  | { kind: 'credentials' }
  | { kind: 'mfa'; via: 'totp' | 'email' }
  /*
   * `enrol` says who asked for this step. Cognito asks (a challenge) when the pool
   * requires MFA; the portal asks (post sign-in) when the pool does not but the portal
   * does. The two are confirmed through different calls, so the form has to know.
   */
  | { kind: 'setupMfa'; uri: string; secret: string; enrol: 'challenge' | 'portal' }
  | { kind: 'newPassword' }
  /** Signed in with a password and no second factor yet: passkey, app, or skip. */
  | { kind: 'secure' };

interface AuthState {
  status: 'loading' | 'signedIn' | 'signedOut';
  email: string | null;
  step: Step;
  endReason: EndReason;
  /** Seconds left before an idle sign-out, or null when the warning is not showing. */
  idleWarning: number | null;
}

interface AuthContextValue extends AuthState {
  configured: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  /** Sign in with a passkey registered to this address. */
  signInWithPasskey: (email: string) => Promise<void>;
  submitChallenge: (value: string) => Promise<void>;
  /** From the "secure your account" step. */
  chooseAuthenticatorApp: () => Promise<void>;
  addPasskeyAndContinue: () => Promise<void>;
  skipSecondFactor: () => Promise<void>;
  signOut: (reason?: EndReason) => Promise<void>;
  resetStep: () => void;
  keepAlive: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Translate Amplify's next-step union into ours. */
function stepFromOutput(output: SignInOutput): Step | 'done' {
  const next = output.nextStep;

  switch (next.signInStep) {
    case 'DONE':
      return 'done';
    case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
      return { kind: 'mfa', via: 'totp' };
    case 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE':
      return { kind: 'mfa', via: 'email' };
    case 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP':
      return {
        kind: 'setupMfa',
        uri: next.totpSetupDetails.getSetupUri('HH Electrical Systems').toString(),
        secret: next.totpSetupDetails.sharedSecret,
        enrol: 'challenge',
      };
    case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
      return { kind: 'newPassword' };
    case 'CONTINUE_SIGN_IN_WITH_FIRST_FACTOR_SELECTION':
      /*
       * Asked for a passkey sign-in, Cognito answers with a choice of factors when this
       * address has no passkey registered. Say that plainly rather than offering a menu
       * whose only useful item is the password box they came from.
       */
      throw Object.assign(new Error('No passkey for this account'), { name: 'NoPasskeyException' });
    default:
      /*
       * SMS, custom challenges, hosted-UI redirects and "select an MFA type" are all
       * reachable in principle and none are configured here. Falling through to the
       * credentials step would silently loop the user back to the password box with no
       * explanation, so this throws and the form shows an error instead.
       */
      throw Object.assign(new Error(`Unsupported sign-in step: ${next.signInStep}`), {
        name: 'UnsupportedSignInStep',
      });
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    status: 'loading',
    email: null,
    step: { kind: 'credentials' },
    endReason: null,
    idleWarning: null,
  });

  /*
   * Kept in a ref as well as in state because the idle timer reads it from inside a
   * setTimeout closure, where a state value would be whatever it was when the timer
   * was scheduled.
   */
  const signedInRef = useRef(false);
  const idleTimer = useRef<number | null>(null);
  const warnTimer = useRef<number | null>(null);
  const countdown = useRef<number | null>(null);
  const absoluteTimer = useRef<number | null>(null);

  const clearTimers = useCallback(() => {
    for (const t of [idleTimer, warnTimer, countdown, absoluteTimer]) {
      if (t.current !== null) {
        window.clearInterval(t.current);
        window.clearTimeout(t.current);
        t.current = null;
      }
    }
  }, []);

  const endSession = useCallback(
    async (reason: EndReason = null) => {
      clearTimers();
      // Before anything is torn down: the API call needs the session still alive.
      await forgetThisBrowser();
      signedInRef.current = false;

      try {
        sessionStorage.removeItem(SESSION_START_KEY);
        if (reason) sessionStorage.setItem(SESSION_END_REASON_KEY, reason);
      } catch {
        // Private mode, or storage disabled. The sign-out itself still matters.
      }

      try {
        // `global` revokes the refresh token at Cognito, so a token copied out of this
        // browser stops working too. Without it, signing out only clears local state.
        if (authConfigured) await amplifySignOut({ global: true });
      } catch {
        // A network failure must not strand someone in a half-signed-out state.
      }

      setState({
        status: 'signedOut',
        email: null,
        step: { kind: 'credentials' },
        endReason: reason,
        idleWarning: null,
      });
    },
    [clearTimers],
  );

  /** Restart the inactivity clock. Called on real interaction, not on every render. */
  const keepAlive = useCallback(() => {
    if (!signedInRef.current) return;

    if (idleTimer.current !== null) window.clearTimeout(idleTimer.current);
    if (warnTimer.current !== null) window.clearTimeout(warnTimer.current);
    if (countdown.current !== null) window.clearInterval(countdown.current);

    setState((s) => (s.idleWarning === null ? s : { ...s, idleWarning: null }));

    warnTimer.current = window.setTimeout(() => {
      let left = Math.floor(IDLE_WARNING_MS / 1000);
      setState((s) => ({ ...s, idleWarning: left }));
      countdown.current = window.setInterval(() => {
        left -= 1;
        setState((s) => ({ ...s, idleWarning: Math.max(left, 0) }));
      }, 1000);
    }, IDLE_LIMIT_MS - IDLE_WARNING_MS);

    idleTimer.current = window.setTimeout(() => {
      void endSession('idle');
    }, IDLE_LIMIT_MS);
  }, [endSession]);

  /** Start (or resume) the absolute deadline. Survives a reload; a new sign-in resets it. */
  const armAbsolute = useCallback(
    (startedAt: number) => {
      if (absoluteTimer.current !== null) window.clearTimeout(absoluteTimer.current);

      const remaining = startedAt + ABSOLUTE_LIMIT_MS - Date.now();
      if (remaining <= 0) {
        void endSession('absolute');
        return;
      }
      absoluteTimer.current = window.setTimeout(() => {
        void endSession('absolute');
      }, remaining);
    },
    [endSession],
  );

  /**
   * Offer a second factor after a password sign-in — offer, not require.
   *
   * Anyone who signs in with only a password and has neither an authenticator app nor
   * a passkey is shown a choice before the portal: add a passkey (recommended), set up
   * an authenticator app, or skip for now. Skipping is allowed and asked again at the
   * next password sign-in, which is the nudge; both can also be set up at any time
   * from Settings → Account.
   *
   * Anyone who already has either is not asked. A passkey sign-in never reaches this
   * at all: the passkey is already two factors.
   *
   * Returns true when the session may begin, false when the choice was put on screen.
   */
  const offerSecondFactor = useCallback(async (email: string): Promise<boolean> => {
    try {
      const [preference, passkeys] = await Promise.all([
        fetchMFAPreference(),
        listWebAuthnCredentials(),
      ]);
      if (preference.enabled?.includes('TOTP')) return true;
      if (passkeys.credentials.length > 0) return true;
    } catch {
      // If Cognito cannot say, do not stand between someone and their work.
      return true;
    }

    setState((s) => ({ ...s, status: 'signedOut', email, step: { kind: 'secure' } }));
    return false;
  }, []);

  const beginSession = useCallback(
    (email: string, startedAt: number) => {
      signedInRef.current = true;
      try {
        sessionStorage.setItem(SESSION_START_KEY, String(startedAt));
        sessionStorage.removeItem(SESSION_END_REASON_KEY);
      } catch {
        // See endSession: storage being unavailable weakens the absolute limit across
        // reloads but must not prevent signing in.
      }
      setState({
        status: 'signedIn',
        email,
        step: { kind: 'credentials' },
        endReason: null,
        idleWarning: null,
      });
      armAbsolute(startedAt);
      keepAlive();
    },
    [armAbsolute, keepAlive],
  );

  // --- Wire the API client to this session ---------------------------------
  //
  // The API client must never import Amplify itself: it is used by tests and by the
  // offline scan page, and pulling the auth SDK into those bundles for a header is
  // both slow and circular. It asks for a token; this decides what that means.
  useEffect(() => {
    setTokenProvider(async () => {
      if (!authConfigured) return DEV_USER ? { devUser: DEV_USER } : null;

      try {
        const session = await fetchAuthSession();
        /*
         * The ID token, not the access token. API Gateway's JWT authorizer accepts
         * either, but the backend maps a caller to an employee row by email on first
         * sign-in, and only the ID token carries `email`. With an access token that
         * lookup finds nothing and every first sign-in is a 401.
         */
        const token = session.tokens?.idToken?.toString();
        return token ? { token } : null;
      } catch {
        return null;
      }
    });
  }, []);

  // --- Restore an existing session on load ---------------------------------
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      if (!authConfigured) {
        // Local development against a backend with ALLOW_DEV_AUTH, or a misconfigured
        // build. Either way there is no Cognito session to restore.
        if (!cancelled) {
          setState((s) => ({
            ...s,
            status: DEV_USER ? 'signedIn' : 'signedOut',
            email: DEV_USER ?? null,
          }));
          if (DEV_USER) signedInRef.current = true;
        }
        return;
      }

      let endReason: EndReason = null;
      try {
        const stored = sessionStorage.getItem(SESSION_END_REASON_KEY);
        if (stored === 'idle' || stored === 'absolute') endReason = stored;
      } catch {
        /* storage unavailable */
      }

      try {
        const user = await getCurrentUser();
        const session = await fetchAuthSession();
        if (cancelled) return;

        if (!session.tokens?.idToken) {
          setState((s) => ({ ...s, status: 'signedOut', endReason }));
          return;
        }

        let startedAt = Date.now();
        try {
          const stored = sessionStorage.getItem(SESSION_START_KEY);
          // A reload must not extend the five hours; only a fresh sign-in may.
          if (stored && Number.isFinite(Number(stored))) startedAt = Number(stored);
        } catch {
          /* storage unavailable */
        }

        beginSession(user.signInDetails?.loginId ?? user.username, startedAt);
      } catch {
        if (!cancelled) setState((s) => ({ ...s, status: 'signedOut', endReason }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [beginSession]);

  // --- Activity listeners ---------------------------------------------------
  useEffect(() => {
    if (state.status !== 'signedIn') return;

    // Pointer, keyboard and scroll. Not mousemove: it fires hundreds of times a second
    // and would reset the timer while someone's sleeve rests on a trackpad.
    const events: (keyof WindowEventMap)[] = ['pointerdown', 'keydown', 'wheel'];

    let throttled = false;
    const onActivity = () => {
      if (throttled) return;
      throttled = true;
      window.setTimeout(() => {
        throttled = false;
      }, 5_000);
      keepAlive();
    };

    for (const e of events) window.addEventListener(e, onActivity, { passive: true });
    return () => {
      for (const e of events) window.removeEventListener(e, onActivity);
    };
  }, [state.status, keepAlive]);

  useEffect(() => clearTimers, [clearTimers]);

  // --- Actions --------------------------------------------------------------

  const signIn = useCallback(
    async (email: string, password: string) => {
      if (!authConfigured) {
        throw Object.assign(new Error('Auth not configured'), { name: 'NotConfigured' });
      }

      /*
       * Cognito refuses a second signIn while one is in progress, which is exactly what
       * happens when someone submits the form twice or returns to it after abandoning
       * an MFA challenge. Clearing first turns a confusing failure into a clean retry.
       */
      try {
        await amplifySignOut();
      } catch {
        /* nothing to clear */
      }

      const output = await amplifySignIn({ username: email, password });
      const step = stepFromOutput(output);

      if (step === 'done') {
        if (await offerSecondFactor(email)) beginSession(email, Date.now());
        return;
      }
      setState((s) => ({ ...s, status: 'signedOut', email, step, endReason: null }));
    },
    [beginSession, offerSecondFactor],
  );

  const submitChallenge = useCallback(
    async (value: string) => {
      /*
       * Portal-driven enrolment is not a Cognito challenge: the sign-in already
       * finished, so the code is verified against the freshly set-up secret and the
       * preference recorded, and only then does the session start.
       */
      if (state.step.kind === 'setupMfa' && state.step.enrol === 'portal') {
        await verifyTOTPSetup({ code: value });
        await updateMFAPreference({ totp: 'PREFERRED' });
        let email = state.email;
        if (!email) {
          const user = await getCurrentUser();
          email = user.signInDetails?.loginId ?? user.username;
        }
        beginSession(email, Date.now());
        return;
      }

      const output = await confirmSignIn({ challengeResponse: value });
      const step = stepFromOutput(output);

      if (step === 'done') {
        /*
         * The email is normally carried over from the credentials step. Ask Cognito
         * only if it is missing — which happens when the page was reloaded partway
         * through a challenge and the form state went with it.
         */
        let email = state.email;
        if (!email) {
          const user = await getCurrentUser();
          email = user.signInDetails?.loginId ?? user.username;
        }
        if (await offerSecondFactor(email)) beginSession(email, Date.now());
        return;
      }
      // A challenge can lead to another challenge: a first sign-in sets a new password
      // and is then asked to enrol in MFA.
      setState((s) => ({ ...s, step }));
    },
    [beginSession, offerSecondFactor, state.email, state.step],
  );

  const signInWithPasskey = useCallback(
    async (email: string) => {
      if (!authConfigured) {
        throw Object.assign(new Error('Auth not configured'), { name: 'NotConfigured' });
      }
      try {
        await amplifySignOut();
      } catch {
        /* nothing to clear */
      }

      // USER_AUTH with the passkey preferred: Amplify runs the browser's passkey prompt.
      const output = await amplifySignIn({
        username: email,
        options: { authFlowType: 'USER_AUTH', preferredChallenge: 'WEB_AUTHN' },
      });
      const step = stepFromOutput(output);

      // Straight in: a passkey with user verification is already two factors.
      if (step === 'done') {
        beginSession(email, Date.now());
        return;
      }
      setState((s) => ({ ...s, status: 'signedOut', email, step, endReason: null }));
    },
    [beginSession],
  );

  /** Resolve the signed-in email when the form state no longer holds it. */
  const currentEmail = useCallback(async (): Promise<string> => {
    if (state.email) return state.email;
    const user = await getCurrentUser();
    return user.signInDetails?.loginId ?? user.username;
  }, [state.email]);

  const chooseAuthenticatorApp = useCallback(async () => {
    const email = await currentEmail();
    const setup = await setUpTOTP();
    setState((s) => ({
      ...s,
      step: {
        kind: 'setupMfa',
        uri: setup.getSetupUri('HH Electrical Systems', email).toString(),
        secret: setup.sharedSecret,
        enrol: 'portal',
      },
    }));
  }, [currentEmail]);

  const addPasskeyAndContinue = useCallback(async () => {
    // The browser's own dialog: fingerprint, face, PIN or a security key.
    await associateWebAuthnCredential();
    beginSession(await currentEmail(), Date.now());
  }, [beginSession, currentEmail]);

  const skipSecondFactor = useCallback(async () => {
    beginSession(await currentEmail(), Date.now());
  }, [beginSession, currentEmail]);

  const resetStep = useCallback(() => {
    setState((s) => ({ ...s, step: { kind: 'credentials' }, endReason: null }));
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      configured: authConfigured,
      signIn,
      signInWithPasskey,
      submitChallenge,
      chooseAuthenticatorApp,
      addPasskeyAndContinue,
      skipSecondFactor,
      signOut: endSession,
      resetStep,
      keepAlive,
    }),
    [
      state,
      signIn,
      signInWithPasskey,
      submitChallenge,
      chooseAuthenticatorApp,
      addPasskeyAndContinue,
      skipSecondFactor,
      endSession,
      resetStep,
      keepAlive,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
